/**
 * Error Reporter - Salva erros na tabela app_errors do Supabase
 * Fire-and-forget, não bloqueia UI
 */

import { supabase } from "@/integrations/supabase/client";
import { sentryCaptureException } from "@/lib/sentry";

// Cache de deduplicação (evitar spam em loops de erro)
const reportedErrors = new Map<string, number>();
const DEDUPE_INTERVAL_MS = 10000; // 10 segundos

interface ErrorReport {
  error_type: 'error' | 'unhandledrejection' | 'chunk' | 'storage' | 'network' | string;
  message?: string;
  stack?: string;
  meta?: Record<string, unknown>;
  user_id?: string;
  partner_id?: string;
  org_id?: string;
}

function getDedupeKey(report: ErrorReport): string {
  return `${report.error_type}:${report.message?.slice(0, 100) || 'unknown'}`;
}

function shouldReport(report: ErrorReport): boolean {
  const key = getDedupeKey(report);
  const lastReported = reportedErrors.get(key);
  
  if (lastReported && Date.now() - lastReported < DEDUPE_INTERVAL_MS) {
    return false;
  }
  
  reportedErrors.set(key, Date.now());
  
  // Limpar cache antigo periodicamente
  if (reportedErrors.size > 100) {
    const now = Date.now();
    for (const [k, v] of reportedErrors.entries()) {
      if (now - v > DEDUPE_INTERVAL_MS * 2) {
        reportedErrors.delete(k);
      }
    }
  }
  
  return true;
}

/**
 * Report an error - saves to app_errors table in Supabase
 */
export async function reportError(report: ErrorReport): Promise<void> {
  // Deduplicação
  if (!shouldReport(report)) {
    return;
  }
  
  // Log para console também (útil para debugging local)
  console.warn('[ErrorReporter]', report.error_type, report.message);
  
  try {
    // Truncar campos para evitar erros de tamanho
    const truncatedMessage = report.message?.slice(0, 1000);
    const truncatedStack = report.stack?.slice(0, 5000);
    
    // Inserir na tabela app_errors (fire-and-forget)
    const { error } = await supabase.from('app_errors').insert({
      app_name: 'crm-frontend',
      error_type: report.error_type,
      message: truncatedMessage,
      stack: truncatedStack,
      meta: report.meta ? JSON.parse(JSON.stringify(report.meta)) : null,
      user_id: report.user_id || null,
      partner_id: report.partner_id || null,
      org_id: report.org_id || null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      env: import.meta.env.MODE || 'production',
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    });
    
    if (error) {
      // Não propagar erro do reporter - apenas log silencioso
      console.debug('[ErrorReporter] Failed to save:', error.message);
    }

    // Dual report: Sentry (dashboards/alertas)
    sentryCaptureException(
      report.stack ? new Error(report.message) : report.message,
      {
        error_type: report.error_type,
        org_id: report.org_id,
        partner_id: report.partner_id,
        ...report.meta,
      }
    );
  } catch (e) {
    // Falha silenciosa - não queremos que o reporter quebre a app
    console.debug('[ErrorReporter] Exception:', e);
  }
}

// Detectar tipo de erro automaticamente
export function detectErrorType(error: unknown): string {
  if (!(error instanceof Error)) return 'unknown';
  
  const message = error.message.toLowerCase();
  
  if (message.includes('chunk') || message.includes('dynamically imported')) {
    return 'chunk';
  }
  
  if (message.includes('storage') || message.includes('quota')) {
    return 'storage';
  }
  
  if (message.includes('network') || message.includes('fetch') || message.includes('cors')) {
    return 'network';
  }
  
  if (message.includes('auth') || message.includes('unauthorized') || message.includes('401')) {
    return 'auth';
  }
  
  if (message.includes('permission') || message.includes('forbidden') || message.includes('403')) {
    return 'permission';
  }
  
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'timeout';
  }
  
  return error.name || 'error';
}

/**
 * Report error from caught exception
 */
export function reportException(
  error: unknown, 
  context?: { org_id?: string; user_id?: string; partner_id?: string; meta?: Record<string, unknown> }
): void {
  const errorType = detectErrorType(error);
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  
  reportError({
    error_type: errorType,
    message,
    stack,
    ...context,
  });
}

/**
 * Setup global error handlers
 */
export function setupGlobalErrorHandlers(): void {
  // Capturar erros não tratados
  window.addEventListener('error', (event) => {
    reportError({
      error_type: 'error',
      message: event.message,
      stack: event.error?.stack,
      meta: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
  
  // Capturar promises rejeitadas não tratadas
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    reportError({
      error_type: 'unhandledrejection',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  });
  
  console.log('[ErrorReporter] Global handlers configured');
}
